"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PopulatableEthersLiquity = exports.PopulatedEthersRedemption = exports.PopulatedEthersLiquityTransaction = exports.SentEthersLiquityTransaction = exports.EthersTransactionCancelledError = exports.getContractAddressByCollateralKey = exports._RawErrorReason = exports._redeemMaxIterations = void 0;
const assert_1 = __importDefault(require("assert"));
const constants_1 = require("@ethersproject/constants");
const logger_1 = require("@ethersproject/logger");
const lib_base_1 = require("@liquity/lib-base");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const _utils_1 = require("./_utils");
const contracts_1 = require("./contracts");
const parseLogs_1 = require("./parseLogs");
const bigNumberMax = (a, b) => ((b === null || b === void 0 ? void 0 : b.gt(a)) ? b : a);
// With 70 iterations redemption costs about ~10M gas, and each iteration accounts for ~138k more
/** @internal */
exports._redeemMaxIterations = 70;
const defaultBorrowingRateSlippageTolerance = lib_base_1.Decimal.from(0.005); // 0.5%
const defaultRedemptionRateSlippageTolerance = lib_base_1.Decimal.from(0.001); // 0.1%
const defaultBorrowingFeeDecayToleranceMinutes = 10;
const noDetails = () => undefined;
const compose = (f, g) => (_) => f(g(_));
const id = (t) => t;
// Takes ~6-7K (use 10K to be safe) to update lastFeeOperationTime, but the cost of calculating the
// decayed baseRate increases logarithmically with time elapsed since the last update.
const addGasForBaseRateUpdate = (maxMinutesSinceLastUpdate = 10) => (gas) => gas.add(10000 + 1414 * Math.ceil(Math.log2(maxMinutesSinceLastUpdate + 1)));
// First traversal in ascending direction takes ~50K, then ~13.5K per extra step.
// 80K should be enough for 3 steps, plus some extra to be safe.
const addGasForPotentialListTraversal = (gas) => gas.add(80000);
const addGasForPSYIssuance = (gas) => gas.add(50000);
const addGasForUnipoolRewardUpdate = (gas) => gas.add(20000);
// To get the best entropy available, we'd do something like:
//
// const bigRandomNumber = () =>
//   BigNumber.from(
//     `0x${Array.from(crypto.getRandomValues(new Uint32Array(8)))
//       .map(u32 => u32.toString(16).padStart(8, "0"))
//       .join("")}`
//   );
//
// However, Window.crypto is browser-specific. Since we only use this for randomly picking Troves
// during the search for hints, Math.random() will do fine, too.
//
// This returns a random integer between 0 and Number.MAX_SAFE_INTEGER
const randomInteger = () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
// Maximum number of trials to perform in a single getApproxHint() call. If the number of trials
// required to get a statistically "good" hint is larger than this, the search for the hint will
// be broken up into multiple getApproxHint() calls.
//
// This should be low enough to work with popular public Ethereum providers like Infura without
// triggering any fair use limits.
const maxNumberOfTrialsAtOnce = 2500;
function* generateTrials(totalNumberOfTrials) {
    assert_1.default(Number.isInteger(totalNumberOfTrials) && totalNumberOfTrials > 0);
    while (totalNumberOfTrials) {
        const numberOfTrials = Math.min(totalNumberOfTrials, maxNumberOfTrialsAtOnce);
        yield numberOfTrials;
        totalNumberOfTrials -= numberOfTrials;
    }
}
/** @internal */
var _RawErrorReason;
(function (_RawErrorReason) {
    _RawErrorReason["TRANSACTION_FAILED"] = "transaction failed";
    _RawErrorReason["TRANSACTION_CANCELLED"] = "cancelled";
    _RawErrorReason["TRANSACTION_REPLACED"] = "replaced";
    _RawErrorReason["TRANSACTION_REPRICED"] = "repriced";
})(_RawErrorReason = exports._RawErrorReason || (exports._RawErrorReason = {}));
const transactionReplacementReasons = [
    _RawErrorReason.TRANSACTION_CANCELLED,
    _RawErrorReason.TRANSACTION_REPLACED,
    _RawErrorReason.TRANSACTION_REPRICED
];
const hasProp = (o, p) => p in o;
const isTransactionFailedError = (error) => hasProp(error, "code") &&
    error.code === logger_1.ErrorCode.CALL_EXCEPTION &&
    hasProp(error, "reason") &&
    error.reason === _RawErrorReason.TRANSACTION_FAILED;
const isTransactionReplacedError = (error) => hasProp(error, "code") &&
    error.code === logger_1.ErrorCode.TRANSACTION_REPLACED &&
    hasProp(error, "reason") &&
    transactionReplacementReasons.includes(error.reason);
const getContractAddressByCollateralKey = (connection, collateralKey) => {
    const { sfrxEthToken, stabilityPoolSfrxEth, wstEthToken, stabilityPoolWstEth } = connection.addresses;
    switch (collateralKey) {
        case "sfrxEthToken":
            return { assetAddress: sfrxEthToken, spAddress: stabilityPoolSfrxEth };
        case "wstEthToken":
            return { assetAddress: wstEthToken, spAddress: stabilityPoolWstEth };
        default:
            return { assetAddress: undefined, spAddress: undefined };
    }
};
exports.getContractAddressByCollateralKey = getContractAddressByCollateralKey;
/**
 * Thrown when a transaction is cancelled or replaced by a different transaction.
 *
 * @public
 */
class EthersTransactionCancelledError extends Error {
    /** @internal */
    constructor(rawError) {
        assert_1.default(rawError.reason !== _RawErrorReason.TRANSACTION_REPRICED);
        super(`Transaction ${rawError.reason}`);
        this.name = "TransactionCancelledError";
        this.rawReplacementReceipt = rawError.receipt;
        this.rawError = rawError;
    }
}
exports.EthersTransactionCancelledError = EthersTransactionCancelledError;
/**
 * A transaction that has already been sent.
 *
 * @remarks
 * Returned by {@link SendableEthersLiquity} functions.
 *
 * @public
 */
class SentEthersLiquityTransaction {
    /** @internal */
    constructor(rawSentTransaction, connection, parse) {
        this.rawSentTransaction = rawSentTransaction;
        this._connection = connection;
        this._parse = parse;
    }
    _receiptFrom(rawReceipt) {
        return rawReceipt
            ? rawReceipt.status
                ? lib_base_1._successfulReceipt(rawReceipt, this._parse(rawReceipt), () => parseLogs_1.logsToString(rawReceipt, EthersLiquityConnection_1._getContracts(this._connection)))
                : lib_base_1._failedReceipt(rawReceipt)
            : lib_base_1._pendingReceipt;
    }
    async _waitForRawReceipt(confirmations) {
        try {
            return await this.rawSentTransaction.wait(confirmations);
        }
        catch (error) {
            if (error instanceof Error) {
                if (isTransactionFailedError(error)) {
                    return error.receipt;
                }
                if (isTransactionReplacedError(error)) {
                    if (error.cancelled) {
                        throw new EthersTransactionCancelledError(error);
                    }
                    else {
                        return error.receipt;
                    }
                }
            }
            throw error;
        }
    }
    /** {@inheritDoc @liquity/lib-base#SentLiquityTransaction.getReceipt} */
    async getReceipt() {
        return this._receiptFrom(await this._waitForRawReceipt(0));
    }
    /**
     * {@inheritDoc @liquity/lib-base#SentLiquityTransaction.waitForReceipt}
     *
     * @throws
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    async waitForReceipt() {
        const receipt = this._receiptFrom(await this._waitForRawReceipt());
        assert_1.default(receipt.status !== "pending");
        return receipt;
    }
}
exports.SentEthersLiquityTransaction = SentEthersLiquityTransaction;
const normalizeBorrowingOperationOptionalParams = (maxBorrowingRateOrOptionalParams, currentBorrowingRate) => {
    var _a, _b;
    if (maxBorrowingRateOrOptionalParams === undefined) {
        return {
            maxBorrowingRate: (_a = currentBorrowingRate === null || currentBorrowingRate === void 0 ? void 0 : currentBorrowingRate.add(defaultBorrowingRateSlippageTolerance)) !== null && _a !== void 0 ? _a : lib_base_1.Decimal.ZERO,
            borrowingFeeDecayToleranceMinutes: defaultBorrowingFeeDecayToleranceMinutes
        };
    }
    else if (typeof maxBorrowingRateOrOptionalParams === "number" ||
        typeof maxBorrowingRateOrOptionalParams === "string" ||
        maxBorrowingRateOrOptionalParams instanceof lib_base_1.Decimal) {
        return {
            maxBorrowingRate: lib_base_1.Decimal.from(maxBorrowingRateOrOptionalParams),
            borrowingFeeDecayToleranceMinutes: defaultBorrowingFeeDecayToleranceMinutes
        };
    }
    else {
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = maxBorrowingRateOrOptionalParams;
        return {
            maxBorrowingRate: maxBorrowingRate !== undefined
                ? lib_base_1.Decimal.from(maxBorrowingRate)
                : (_b = currentBorrowingRate === null || currentBorrowingRate === void 0 ? void 0 : currentBorrowingRate.add(defaultBorrowingRateSlippageTolerance)) !== null && _b !== void 0 ? _b : lib_base_1.Decimal.ZERO,
            borrowingFeeDecayToleranceMinutes: borrowingFeeDecayToleranceMinutes !== null && borrowingFeeDecayToleranceMinutes !== void 0 ? borrowingFeeDecayToleranceMinutes : defaultBorrowingFeeDecayToleranceMinutes
        };
    }
};
/**
 * A transaction that has been prepared for sending.
 *
 * @remarks
 * Returned by {@link PopulatableEthersLiquity} functions.
 *
 * @public
 */
class PopulatedEthersLiquityTransaction {
    /** @internal */
    constructor(rawPopulatedTransaction, connection, parse, gasHeadroom) {
        this.rawPopulatedTransaction = rawPopulatedTransaction;
        this._connection = connection;
        this._parse = parse;
        if (gasHeadroom !== undefined) {
            this.gasHeadroom = gasHeadroom;
        }
    }
    /** {@inheritDoc @liquity/lib-base#PopulatedLiquityTransaction.send} */
    async send() {
        return new SentEthersLiquityTransaction(await EthersLiquityConnection_1._requireSigner(this._connection).sendTransaction(this.rawPopulatedTransaction), this._connection, this._parse);
    }
}
exports.PopulatedEthersLiquityTransaction = PopulatedEthersLiquityTransaction;
/**
 * {@inheritDoc @liquity/lib-base#PopulatedRedemption}
 *
 * @public
 */
class PopulatedEthersRedemption extends PopulatedEthersLiquityTransaction {
    /** @internal */
    constructor(rawPopulatedTransaction, connection, attemptedSLSDAmount, redeemableSLSDAmount, increaseAmountByMinimumNetDebt) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(connection);
        super(rawPopulatedTransaction, connection, ({ logs }) => troveManager
            .extractEvents(logs, "Redemption")
            .map(({ args: { _AssetSent, _AssetFee, _actualSLSDAmount, _attemptedSLSDAmount } }) => ({
            attemptedSLSDAmount: _utils_1.decimalify(_attemptedSLSDAmount),
            actualSLSDAmount: _utils_1.decimalify(_actualSLSDAmount),
            collateralTaken: _utils_1.decimalify(_AssetSent),
            fee: _utils_1.decimalify(_AssetFee)
        }))[0]);
        this.attemptedSLSDAmount = attemptedSLSDAmount;
        this.redeemableSLSDAmount = redeemableSLSDAmount;
        this.isTruncated = redeemableSLSDAmount.lt(attemptedSLSDAmount);
        this._increaseAmountByMinimumNetDebt = increaseAmountByMinimumNetDebt;
    }
    /** {@inheritDoc @liquity/lib-base#PopulatedRedemption.increaseAmountByMinimumNetDebt} */
    increaseAmountByMinimumNetDebt(maxRedemptionRate) {
        if (!this._increaseAmountByMinimumNetDebt) {
            throw new Error("PopulatedEthersRedemption: increaseAmountByMinimumNetDebt() can " +
                "only be called when amount is truncated");
        }
        return this._increaseAmountByMinimumNetDebt(maxRedemptionRate);
    }
}
exports.PopulatedEthersRedemption = PopulatedEthersRedemption;
/**
 * Ethers-based implementation of {@link @liquity/lib-base#PopulatableLiquity}.
 *
 * @public
 */
class PopulatableEthersLiquity {
    constructor(readable) {
        this._readable = readable;
    }
    _wrapSimpleTransaction(rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, noDetails);
    }
    _wrapTroveChangeWithFees(params, rawPopulatedTransaction, gasHeadroom) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => {
            const [newTrove] = borrowerOperations
                .extractEvents(logs, "TroveUpdated")
                .map(({ args: { _coll, _debt } }) => new lib_base_1.Trove(_utils_1.decimalify(_coll), _utils_1.decimalify(_debt)));
            const [fee] = borrowerOperations
                .extractEvents(logs, "SLSDBorrowingFeePaid")
                .map(({ args: { _SLSDFee } }) => _utils_1.decimalify(_SLSDFee));
            return {
                params,
                newTrove,
                fee
            };
        }, gasHeadroom);
    }
    async _wrapTroveClosure(rawPopulatedTransaction) {
        const { activePool, slsdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs, from: userAddress }) => {
            const [repaySLSD] = slsdToken
                .extractEvents(logs, "Transfer")
                .filter(({ args: { from, to } }) => from === userAddress && to === constants_1.AddressZero)
                .map(({ args: { value } }) => _utils_1.decimalify(value));
            const [withdrawCollateral] = activePool
                .extractEvents(logs, "AssetSent")
                .filter(({ args: { _to } }) => _to === userAddress)
                .map(({ args: { _amount } }) => _utils_1.decimalify(_amount));
            return {
                params: repaySLSD.nonZero ? { withdrawCollateral, repaySLSD } : { withdrawCollateral }
            };
        });
    }
    _wrapLiquidation(rawPopulatedTransaction) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => {
            const liquidatedAddresses = troveManager
                .extractEvents(logs, "TroveLiquidated")
                .map(({ args: { _borrower } }) => _borrower);
            const [totals] = troveManager
                .extractEvents(logs, "Liquidation")
                .map(({ args: { _SLSDGasCompensation, _collGasCompensation, _liquidatedColl, _liquidatedDebt } }) => ({
                collateralGasCompensation: _utils_1.decimalify(_collGasCompensation),
                slsdGasCompensation: _utils_1.decimalify(_SLSDGasCompensation),
                totalLiquidated: new lib_base_1.Trove(_utils_1.decimalify(_liquidatedColl), _utils_1.decimalify(_liquidatedDebt))
            }));
            return {
                liquidatedAddresses,
                ...totals
            };
        });
    }
    _extractStabilityPoolGainsWithdrawalDetails(stabilityPool, logs) {
        const [newSLSDDeposit] = stabilityPool
            .extractEvents(logs, "UserDepositChanged")
            .map(({ args: { _newDeposit } }) => _utils_1.decimalify(_newDeposit));
        const [[collateralGain, slsdLoss]] = stabilityPool
            .extractEvents(logs, "AssetGainWithdrawn")
            .map(({ args: { _Asset, _SLSDLoss } }) => [_utils_1.decimalify(_Asset), _utils_1.decimalify(_SLSDLoss)]);
        const [psyReward] = stabilityPool
            .extractEvents(logs, "PSYPaidToDepositor")
            .map(({ args: { _PSY } }) => _utils_1.decimalify(_PSY));
        return {
            slsdLoss,
            newSLSDDeposit,
            collateralGain,
            psyReward
        };
    }
    _wrapStabilityPoolGainsWithdrawal(stabilityPool, rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => this._extractStabilityPoolGainsWithdrawalDetails(stabilityPool, logs));
    }
    _wrapStabilityDepositTopup(stabilityPool, change, rawPopulatedTransaction) {
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => ({
            ...this._extractStabilityPoolGainsWithdrawalDetails(stabilityPool, logs),
            change
        }));
    }
    async _wrapStabilityDepositWithdrawal(stabilityPool, rawPopulatedTransaction) {
        const { slsdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs, from: userAddress }) => {
            const gainsWithdrawalDetails = this._extractStabilityPoolGainsWithdrawalDetails(stabilityPool, logs);
            const [withdrawSLSD] = slsdToken
                .extractEvents(logs, "Transfer")
                .filter(({ args: { from, to } }) => from === stabilityPool.address && to === userAddress)
                .map(({ args: { value } }) => _utils_1.decimalify(value));
            return {
                ...gainsWithdrawalDetails,
                change: { withdrawSLSD, withdrawAllSLSD: gainsWithdrawalDetails.newSLSDDeposit.isZero }
            };
        });
    }
    _wrapCollateralGainTransfer(stabilityPool, rawPopulatedTransaction) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return new PopulatedEthersLiquityTransaction(rawPopulatedTransaction, this._readable.connection, ({ logs }) => {
            const [newTrove] = borrowerOperations
                .extractEvents(logs, "TroveUpdated")
                .map(({ args: { _coll, _debt } }) => new lib_base_1.Trove(_utils_1.decimalify(_coll), _utils_1.decimalify(_debt)));
            return {
                ...this._extractStabilityPoolGainsWithdrawalDetails(stabilityPool, logs),
                newTrove
            };
        });
    }
    async _findHintsForNominalCollateralRatio(nominalCollateralRatio, assetAddress, ownAddress) {
        const { sortedTroves, hintHelpers } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const numberOfTroves = await this._readable.getNumberOfTroves(assetAddress);
        if (!numberOfTroves) {
            return [constants_1.AddressZero, constants_1.AddressZero];
        }
        if (nominalCollateralRatio.infinite) {
            return [constants_1.AddressZero, await sortedTroves.getFirst(assetAddress)];
        }
        const totalNumberOfTrials = Math.ceil(10 * Math.sqrt(numberOfTroves));
        const [firstTrials, ...restOfTrials] = generateTrials(totalNumberOfTrials);
        const collectApproxHint = ({ latestRandomSeed, results }, numberOfTrials) => hintHelpers
            .getApproxHint(assetAddress, nominalCollateralRatio.hex, numberOfTrials, latestRandomSeed)
            .then(({ latestRandomSeed, ...result }) => ({
            latestRandomSeed,
            results: [...results, result]
        }));
        const { results } = await restOfTrials.reduce((p, numberOfTrials) => p.then(state => collectApproxHint(state, numberOfTrials)), collectApproxHint({ latestRandomSeed: randomInteger(), results: [] }, firstTrials));
        const { hintAddress } = results.reduce((a, b) => (a.diff.lt(b.diff) ? a : b));
        let [prev, next] = await sortedTroves.findInsertPosition(assetAddress, nominalCollateralRatio.hex, hintAddress, hintAddress);
        if (ownAddress) {
            // In the case of reinsertion, the address of the Trove being reinserted is not a usable hint,
            // because it is deleted from the list before the reinsertion.
            // "Jump over" the Trove to get the proper hint.
            if (prev === ownAddress) {
                prev = await sortedTroves.getPrev(assetAddress, prev);
            }
            else if (next === ownAddress) {
                next = await sortedTroves.getNext(assetAddress, next);
            }
        }
        // Don't use `address(0)` as hint as it can result in huge gas cost.
        // (See https://github.com/liquity/dev/issues/600).
        if (prev === constants_1.AddressZero) {
            prev = next;
        }
        else if (next === constants_1.AddressZero) {
            next = prev;
        }
        return [prev, next];
    }
    async _findHints(trove, assetAddress, ownAddress) {
        if (trove instanceof lib_base_1.TroveWithPendingRedistribution) {
            throw new Error("Rewards must be applied to this Trove");
        }
        return this._findHintsForNominalCollateralRatio(trove._nominalCollateralRatio, assetAddress, ownAddress);
    }
    async _findRedemptionHints(amount, assetAddress) {
        const { hintHelpers } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const price = await this._readable.getPrice(assetAddress);
        const { firstRedemptionHint, partialRedemptionHintNICR, truncatedSLSDamount } = await hintHelpers.getRedemptionHints(assetAddress, amount.hex, price.hex, exports._redeemMaxIterations);
        const [partialRedemptionUpperHint, partialRedemptionLowerHint] = partialRedemptionHintNICR.isZero()
            ? [constants_1.AddressZero, constants_1.AddressZero]
            : await this._findHintsForNominalCollateralRatio(_utils_1.decimalify(partialRedemptionHintNICR), assetAddress
            // XXX: if we knew the partially redeemed Trove's address, we'd pass it here
            );
        return [
            _utils_1.decimalify(truncatedSLSDamount),
            firstRedemptionHint,
            partialRedemptionUpperHint,
            partialRedemptionLowerHint,
            partialRedemptionHintNICR
        ];
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.openTrove} */
    async openTrove(assetAddress, params, maxBorrowingRateOrOptionalParams, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const normalizedParams = lib_base_1._normalizeTroveCreation(params);
        const { depositCollateral, borrowSLSD } = normalizedParams;
        const [fees, blockTimestamp, total, price] = await Promise.all([
            this._readable._getFeesFactory(assetAddress),
            this._readable._getBlockTimestamp(),
            this._readable.getTotal(assetAddress),
            this._readable.getPrice(assetAddress)
        ]);
        const recoveryMode = total.collateralRatioIsBelowCritical(price);
        const decayBorrowingRate = (seconds) => fees(blockTimestamp + seconds, recoveryMode).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        const newTrove = lib_base_1.Trove.create(normalizedParams, currentBorrowingRate);
        const hints = await this._findHints(newTrove, assetAddress);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        const txParams = (borrowSLSD) => [
            assetAddress,
            depositCollateral.hex,
            maxBorrowingRate.hex,
            borrowSLSD.hex,
            ...hints,
            // NOTE: Use msg.value if asset is ETH
            // { value: depositCollateral.hex, ...overrides }
            { ...overrides }
        ];
        let gasHeadroom;
        // TODO-PSY:
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            // if (overrides?.gasLimit !== undefined) {
            const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            const decayedTrove = lib_base_1.Trove.create(normalizedParams, decayedBorrowingRate);
            const { borrowSLSD: borrowSLSDSimulatingDecay } = lib_base_1.Trove.recreate(decayedTrove, currentBorrowingRate);
            if (decayedTrove.debt.lt(lib_base_1.SLSD_MINIMUM_DEBT)) {
                throw new Error(`Trove's debt might fall below ${lib_base_1.SLSD_MINIMUM_DEBT} ` +
                    `within ${borrowingFeeDecayToleranceMinutes} minutes`);
            }
            const [gasNow, gasLater] = await Promise.all([
                borrowerOperations.estimateGas.openTrove(...txParams(borrowSLSD)),
                borrowerOperations.estimateGas.openTrove(...txParams(borrowSLSDSimulatingDecay))
            ]);
            const gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater));
            gasHeadroom = gasLimit.sub(gasNow).toNumber();
            overrides = { ...overrides, gasLimit };
        }
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.openTrove(...txParams(borrowSLSD)), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.closeTrove} */
    async closeTrove(assetAddress, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapTroveClosure(await borrowerOperations.estimateAndPopulate.closeTrove({ ...overrides }, id, assetAddress));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.depositCollateral} */
    depositCollateral(assetAddress, amount, overrides) {
        return this.adjustTrove(assetAddress, { depositCollateral: amount }, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawCollateral} */
    withdrawCollateral(assetAddress, amount, overrides) {
        return this.adjustTrove(assetAddress, { withdrawCollateral: amount }, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.borrowSLSD} */
    borrowSLSD(assetAddress, amount, maxBorrowingRate, overrides) {
        return this.adjustTrove(assetAddress, { borrowSLSD: amount }, maxBorrowingRate, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.repaySLSD} */
    repaySLSD(assetAddress, amount, overrides) {
        return this.adjustTrove(assetAddress, { repaySLSD: amount }, undefined, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.adjustTrove} */
    async adjustTrove(assetAddress, params, maxBorrowingRateOrOptionalParams, overrides) {
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        const normalizedParams = lib_base_1._normalizeTroveAdjustment(params);
        const { depositCollateral, withdrawCollateral, borrowSLSD, repaySLSD } = normalizedParams;
        // const assetAddress = getAssetAddress(this._readable.connection, assetName);
        const [trove, feeVars] = await Promise.all([
            this._readable.getTrove(assetAddress, address),
            borrowSLSD &&
                _utils_1.promiseAllValues({
                    fees: this._readable._getFeesFactory(assetAddress),
                    blockTimestamp: this._readable._getBlockTimestamp(),
                    total: this._readable.getTotal(assetAddress),
                    price: this._readable.getPrice(assetAddress)
                })
        ]);
        const decayBorrowingRate = (seconds) => feeVars === null || feeVars === void 0 ? void 0 : feeVars.fees(feeVars.blockTimestamp + seconds, feeVars.total.collateralRatioIsBelowCritical(feeVars.price)).borrowingRate();
        const currentBorrowingRate = decayBorrowingRate(0);
        const adjustedTrove = trove.adjust(normalizedParams, currentBorrowingRate);
        const hints = await this._findHints(adjustedTrove, assetAddress, address);
        const { maxBorrowingRate, borrowingFeeDecayToleranceMinutes } = normalizeBorrowingOperationOptionalParams(maxBorrowingRateOrOptionalParams, currentBorrowingRate);
        const txParams = (borrowSLSD) => {
            var _a;
            return [
                assetAddress,
                (depositCollateral !== null && depositCollateral !== void 0 ? depositCollateral : lib_base_1.Decimal.ZERO).hex,
                maxBorrowingRate.hex,
                (withdrawCollateral !== null && withdrawCollateral !== void 0 ? withdrawCollateral : lib_base_1.Decimal.ZERO).hex,
                ((_a = borrowSLSD !== null && borrowSLSD !== void 0 ? borrowSLSD : repaySLSD) !== null && _a !== void 0 ? _a : lib_base_1.Decimal.ZERO).hex,
                !!borrowSLSD,
                ...hints,
                { ...overrides }
                // { value: depositCollateral?.hex, ...overrides }
            ];
        };
        let gasHeadroom;
        if ((overrides === null || overrides === void 0 ? void 0 : overrides.gasLimit) === undefined) {
            const decayedBorrowingRate = decayBorrowingRate(60 * borrowingFeeDecayToleranceMinutes);
            const decayedTrove = trove.adjust(normalizedParams, decayedBorrowingRate);
            const { borrowSLSD: borrowSLSDSimulatingDecay } = trove.adjustTo(decayedTrove, currentBorrowingRate);
            if (decayedTrove.debt.lt(lib_base_1.SLSD_MINIMUM_DEBT)) {
                throw new Error(`Trove's debt might fall below ${lib_base_1.SLSD_MINIMUM_DEBT} ` +
                    `within ${borrowingFeeDecayToleranceMinutes} minutes`);
            }
            const [gasNow, gasLater] = await Promise.all([
                borrowerOperations.estimateGas.adjustTrove(...txParams(borrowSLSD)),
                borrowSLSD &&
                    borrowerOperations.estimateGas.adjustTrove(...txParams(borrowSLSDSimulatingDecay))
            ]);
            let gasLimit = bigNumberMax(addGasForPotentialListTraversal(gasNow), gasLater);
            if (borrowSLSD) {
                gasLimit = addGasForBaseRateUpdate(borrowingFeeDecayToleranceMinutes)(gasLimit);
            }
            gasHeadroom = gasLimit.sub(gasNow).toNumber();
            overrides = { ...overrides, gasLimit };
        }
        return this._wrapTroveChangeWithFees(normalizedParams, await borrowerOperations.populateTransaction.adjustTrove(...txParams(borrowSLSD)), gasHeadroom);
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.claimCollateralSurplus} */
    async claimCollateralSurplus(assetAddress, overrides) {
        const { borrowerOperations } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await borrowerOperations.estimateAndPopulate.claimCollateral({ ...overrides }, id, assetAddress));
    }
    /** @internal */
    async setPrice(price, overrides) {
        const { priceFeed } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        if (!contracts_1._priceFeedIsTestnet(priceFeed)) {
            throw new Error("setPrice() unavailable on this deployment of Liquity");
        }
        return this._wrapSimpleTransaction(await priceFeed.estimateAndPopulate.setPrice({ ...overrides }, id, lib_base_1.Decimal.from(price).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.liquidate} */
    async liquidate(assetAddress, address, overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        if (Array.isArray(address)) {
            return this._wrapLiquidation(await troveManager.estimateAndPopulate.batchLiquidateTroves({ ...overrides }, addGasForPSYIssuance, assetAddress, address));
        }
        else {
            return this._wrapLiquidation(await troveManager.estimateAndPopulate.liquidate({ ...overrides }, addGasForPSYIssuance, assetAddress, address));
        }
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.liquidateUpTo} */
    async liquidateUpTo(assetAddress, maximumNumberOfTrovesToLiquidate, overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapLiquidation(await troveManager.estimateAndPopulate.liquidateTroves({ ...overrides }, addGasForPSYIssuance, assetAddress, maximumNumberOfTrovesToLiquidate));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.depositSLSDInStabilityPool} */
    async depositSLSDInStabilityPool(spAddress, amount, frontendTag, overrides) {
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this._readable.connection);
        if (!stabilityPool) {
            throw new Error("StabilityPool address is not found. [depositSLSDInStabilityPool]");
        }
        const depositSLSD = lib_base_1.Decimal.from(amount);
        return this._wrapStabilityDepositTopup(stabilityPool, { depositSLSD }, await stabilityPool.estimateAndPopulate.provideToSP({ ...overrides }, addGasForPSYIssuance, depositSLSD.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawSLSDFromStabilityPool} */
    async withdrawSLSDFromStabilityPool(spAddress, amount, overrides) {
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this._readable.connection);
        if (!stabilityPool) {
            throw new Error("StabilityPool address is not found. [withdrawSLSDFromStabilityPool]");
        }
        return this._wrapStabilityDepositWithdrawal(stabilityPool, await stabilityPool.estimateAndPopulate.withdrawFromSP({ ...overrides }, addGasForPSYIssuance, lib_base_1.Decimal.from(amount).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.withdrawGainsFromStabilityPool} */
    async withdrawGainsFromStabilityPool(spAddress, overrides) {
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this._readable.connection);
        if (!stabilityPool) {
            throw new Error("StabilityPool address is not found. [withdrawGainsFromStabilityPool]");
        }
        return this._wrapStabilityPoolGainsWithdrawal(stabilityPool, await stabilityPool.estimateAndPopulate.withdrawFromSP({ ...overrides }, addGasForPSYIssuance, lib_base_1.Decimal.ZERO.hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.transferCollateralGainToTrove} */
    async transferCollateralGainToTrove(spAddress, assetAddress, overrides) {
        const address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides);
        const stabilityPool = EthersLiquityConnection_1._getStabilityPoolContractByAddress(spAddress, this._readable.connection);
        if (!stabilityPool) {
            throw new Error("StabilityPool address is not found. [transferCollateralGainToTrove]");
        }
        const [initialTrove, stabilityDeposit] = await Promise.all([
            this._readable.getTrove(assetAddress, address),
            this._readable.getStabilityDeposit(spAddress, address)
        ]);
        const finalTrove = initialTrove.addCollateral(stabilityDeposit.collateralGain);
        return this._wrapCollateralGainTransfer(stabilityPool, await stabilityPool.estimateAndPopulate.withdrawAssetGainToTrove({ ...overrides }, compose(addGasForPotentialListTraversal, addGasForPSYIssuance), ...(await this._findHints(finalTrove, assetAddress, address))));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.sendSLSD} */
    async sendSLSD(toAddress, amount, overrides) {
        const { slsdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await slsdToken.estimateAndPopulate.transfer({ ...overrides }, id, toAddress, lib_base_1.Decimal.from(amount).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.sendPSY} */
    async sendPSY(toAddress, amount, overrides) {
        const { slsdToken } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        return this._wrapSimpleTransaction(await slsdToken.estimateAndPopulate.transfer({ ...overrides }, id, toAddress, lib_base_1.Decimal.from(amount).hex));
    }
    /** {@inheritDoc @liquity/lib-base#PopulatableLiquity.redeemSLSD} */
    async redeemSLSD(assetAddress, amount, maxRedemptionRate, overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this._readable.connection);
        // const assetAddress = getAssetAddress(this._readable.connection, assetName)
        const attemptedSLSDAmount = lib_base_1.Decimal.from(amount);
        const [fees, total, [truncatedAmount, firstRedemptionHint, ...partialHints]] = await Promise.all([
            this._readable.getFees(assetAddress),
            this._readable.getTotal(assetAddress),
            this._findRedemptionHints(attemptedSLSDAmount, assetAddress)
        ]);
        if (truncatedAmount.isZero) {
            throw new Error(`redeemSLSD: amount too low to redeem (try at least ${lib_base_1.SLSD_MINIMUM_NET_DEBT})`);
        }
        const defaultMaxRedemptionRate = (amount) => lib_base_1.Decimal.min(fees.redemptionRate(amount.div(total.debt)).add(defaultRedemptionRateSlippageTolerance), lib_base_1.Decimal.ONE);
        const populateRedemption = async (attemptedSLSDAmount, maxRedemptionRate, truncatedAmount = attemptedSLSDAmount, partialHints = [constants_1.AddressZero, constants_1.AddressZero, 0]) => {
            const maxRedemptionRateOrDefault = maxRedemptionRate !== undefined
                ? lib_base_1.Decimal.from(maxRedemptionRate)
                : defaultMaxRedemptionRate(truncatedAmount);
            return new PopulatedEthersRedemption(await troveManager.estimateAndPopulate.redeemCollateral({ ...overrides }, addGasForBaseRateUpdate(), assetAddress, truncatedAmount.hex, firstRedemptionHint, ...partialHints, exports._redeemMaxIterations, maxRedemptionRateOrDefault.hex), this._readable.connection, attemptedSLSDAmount, truncatedAmount, truncatedAmount.lt(attemptedSLSDAmount)
                ? newMaxRedemptionRate => populateRedemption(truncatedAmount.add(lib_base_1.SLSD_MINIMUM_NET_DEBT), newMaxRedemptionRate !== null && newMaxRedemptionRate !== void 0 ? newMaxRedemptionRate : maxRedemptionRate)
                : undefined);
        };
        return populateRedemption(attemptedSLSDAmount, maxRedemptionRate, truncatedAmount, partialHints);
    }
    async approveAsset(assetAddress, allowance, overrides) {
        const assetToken = EthersLiquityConnection_1._getERC20ContractByAddress(assetAddress, this._readable.connection);
        if (!assetToken) {
            throw new Error("approeAsset unavailable on this deployment of PSY");
        }
        const { borrowerOperations: borrowerOperationsAddress } = this._readable.connection.addresses;
        return this._wrapSimpleTransaction(await assetToken.estimateAndPopulate.approve({ ...overrides }, id, borrowerOperationsAddress, lib_base_1.Decimal.from(allowance !== null && allowance !== void 0 ? allowance : lib_base_1.Decimal.INFINITY).hex));
    }
    /** @internal */
    async _mintAsset(amount, assetAddress, address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this._readable.connection, overrides));
        const assetToken = EthersLiquityConnection_1._getERC20ContractByAddress(assetAddress, this._readable.connection);
        if (!assetToken || !contracts_1._assetTokenIsMock(assetToken)) {
            throw new Error("_mintAsset() unavailable on this deployment of PSY");
        }
        return this._wrapSimpleTransaction(await assetToken.estimateAndPopulate.mint({ ...overrides }, id, address, lib_base_1.Decimal.from(amount).hex));
    }
}
exports.PopulatableEthersLiquity = PopulatableEthersLiquity;
//# sourceMappingURL=PopulatableEthersLiquity.js.map